import React, { useState } from 'react'
import { Link, useLocation, Navigate } from 'react-router-dom'
import queryString from 'query-string'
import 'twin.macro'
import { FieldValues, useForm } from 'react-hook-form'
import { Message } from '@utils/message'
import { RegExps } from '@utils/regexps'
import StBaseSelect from '@components/shared/StBaseSelect'
import StBaseInput from '@components/shared/StBaseInput'
import StBaseValidLabel from '@components/shared/StBaseValidLabel'
import { useMutation } from 'react-query'
import { api } from '@api/index'
import useModal from '@hooks/useModal'
import MessageModal from '@components/shared/MessageModal'
import { useNavigate } from 'react-router-dom'

const SignUp = () => {
  const [openModal] = useModal()
  const [isConfirmEmail, setIsConfirmEmail] = useState(false)
  const [isConfirmNickname, setIsConfirmNickname] = useState(false)
  const navigate = useNavigate()
  const loc = useLocation()
  const query = queryString.parse(loc.search)
  const isSelectOptionTerms = query.optionalterms as string
  const maxBirthRange = new Date().toISOString().slice(0, 10)

  if (isSelectOptionTerms === undefined) {
    return <Navigate to={'/tos'} />
  }

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    trigger,
    setError,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<any>({ mode: 'onChange', defaultValues: { gender: 'default' } })

  const checkNicknameMutation = useMutation((nickname: string) =>
    api.userService.checkNickname(nickname)
  )

  const signUpMutation = useMutation((signupDTO: SignupReq) =>
    api.userService.signup(signupDTO)
  )

  const sendEmailOTPMutation = useMutation((id: string) =>
    api.userService.sendEmailOTP(id)
  )

  const verificationEmailOTPMutation = useMutation(
    ({ email, code }: FieldValues) =>
      api.userService.verificationEmailOTP(email, code)
  )

  const handleSignUp = (formData: FieldValues) => {
    signUpMutation.mutate(formData as SignupReq, {
      onError: (err) => {
        openModal(<MessageModal errorData={err} />)
      },
      onSuccess: async () => {
        await openModal(
          <MessageModal>{Message.info.SUCCESS_SIGNUP}</MessageModal>
        )
        navigate('/')
      },
    })
  }

  const handleSendEmailCode = async () => {
    const isValid = await trigger('email')
    if (isValid) {
      sendEmailOTPMutation.mutate(getValues('email'), {
        onError: (err) => {
          openModal(<MessageModal errorData={err} />)
        },
        onSuccess: () => {
          openModal(
            <MessageModal>{Message.info.SUCCESS_SEND_EMAIL_OTP}</MessageModal>
          )
        },
      })
    }
  }

  const handleChangeEmail = () => {
    setIsConfirmEmail(false)
    setValue('code', '')
    setError('code', {
      type: 'validate',
      message: Message.valid.REQUIRE_EMAIL_CONFIRM,
    })
  }

  const handleEmailCodeConfirm = async () => {
    const isValid = await trigger('email')
    const DTO = { email: getValues('email'), code: getValues('code') }
    if (isValid) {
      verificationEmailOTPMutation.mutate(DTO, {
        onError: (err) => {
          openModal(<MessageModal errorData={err} />)
        },
        onSuccess: () => {
          setIsConfirmEmail(true)
          clearErrors('code')
        },
      })
    }
  }

  const handleChangeNickname = () => {
    setIsConfirmNickname(false)
    setError('nickname', {
      type: 'validate',
      message: Message.valid.REQUIRE_NICKNAME_CONFIRM,
    })
  }

  const handleNicknameConfirm = async () => {
    checkNicknameMutation.mutate(getValues('nickname'), {
      onError: (err) => {
        openModal(<MessageModal errorData={err} />)
      },
      onSuccess: () => {
        setIsConfirmNickname(true)
        clearErrors('nickname')
      },
    })
  }

  return (
    <section className="w-550">
      <form onSubmit={handleSubmit(handleSignUp)}>
        <div className="text-2xl font-bold mb-15 flex-center ">회원가입</div>
        <div className="flex flex-col w-full gap-12 mb-15">
          <label>아이디</label>
          <input
            {...register('username', {
              required: Message.valid.REQUIRE_ID,
              pattern: {
                value: RegExps.user.ID,
                message: Message.valid.INVALID_ID,
              },
            })}
            type="text"
            tw="h-40"
            placeholder="아이디를 입력해 주세요."
            className="input"
          />
          <div className="flex items-center text-base text-red-500 h-11">
            {errors.username && errors.username.message}
          </div>
          <label>비밀번호</label>
          <input
            {...register('password', {
              required: Message.valid.REQUIRE_PASSWORD,
              pattern: {
                value: RegExps.user.PASSWORD,
                message: Message.valid.INVALID_PASSWORD,
              },
            })}
            type="password"
            tw="h-40"
            placeholder="비밀번호를 입력해 주세요."
            className="input"
          />
          <div className="flex items-center text-base text-red-500 h-11 ">
            {errors.password && errors.password.message}
          </div>
          <label>비밀번호 확인</label>
          <input
            {...register('confirmPassword', {
              required: Message.valid.INVALID_PASSWORD_CONFIRM,
              validate: (value) => value === watch('password'),
            })}
            type="password"
            tw="h-40"
            placeholder="비밀번호를 다시 입력해 주세요."
            className="input"
          />
          <div className="flex items-center text-base text-red-500 h-11 ">
            {errors.confirmPassword && Message.valid.INVALID_PASSWORD_CONFIRM}
          </div>
          <label>이름</label>
          <input
            {...register('name', {
              required: Message.valid.REQUIRE_NAME,
              pattern: {
                value: RegExps.user.NAME,
                message: Message.valid.INVALID_NAME,
              },
            })}
            type="text"
            tw="h-40"
            placeholder="이름을 입력해 주세요."
            className="input"
          />
          <div className="flex items-center text-base text-red-500 h-11 ">
            {errors.name && errors.name.message}
          </div>
          <label>성별</label>
          <StBaseSelect
            {...register('gender', {
              pattern: {
                value: RegExps.user.GENDER,
                message: Message.valid.REQUIRE_GENDER,
              },
            })}
            tw="h-40 px-10 py-0"
            className="input"
            isDefaultValue={watch('gender') === 'default'}
          >
            <option disabled value="default">
              성별을 선택해 주세요.
            </option>
            <option value="MALE">남자</option>
            <option value="FEMALE">여자</option>
          </StBaseSelect>
          <div className="flex items-center text-base text-red-500 h-11 ">
            {errors.gender && errors.gender.message}
          </div>
          <label>생년월일</label>
          <input
            {...register('birth', {
              required: Message.valid.REQUIRE_BIRTH,
            })}
            type="date"
            tw="h-40"
            className="input"
            max={`${maxBirthRange}`}
          />
          <div className="flex items-center text-base text-red-500 h-11 ">
            {errors.birth && errors.birth.message}
          </div>
          <label>휴대폰</label>
          <input
            {...register('phone', {
              required: Message.valid.REQUIRE_PHONE,
              pattern: {
                value: RegExps.user.PHONE,
                message: Message.valid.INVALID_PHONE,
              },
            })}
            placeholder="휴대폰 번호를 입력해 주세요."
            type="number"
            tw="h-40"
            className="input"
          />
          <div className="flex items-center text-base text-red-500 h-11 ">
            {errors.phone && errors.phone.message}
          </div>
          <label>이메일 주소</label>
          <div className="flex gap-20">
            <StBaseInput
              {...register('email', {
                onChange: handleChangeEmail,
                required: Message.valid.REQUIRE_EMAIL,
                pattern: {
                  value: RegExps.user.EMAIL,
                  message: Message.valid.INVALID_EMAIL,
                },
              })}
              placeholder="이메일을 입력해 주세요."
              type="text"
              tw="h-40 w-full"
              className="input"
            />
            <button
              onClick={handleSendEmailCode}
              type="button"
              tw="text-base w-160"
              className="button base"
            >
              인증메일 발송
            </button>
          </div>
          <div className="flex items-center text-base text-red-500 h-11 ">
            {errors.email && errors.email.message}
          </div>
          <label>이메일 인증 코드</label>
          <div className="flex gap-20">
            <StBaseInput
              {...register('code', {
                validate: () =>
                  isConfirmEmail || Message.valid.REQUIRE_EMAIL_CONFIRM,
              })}
              placeholder="이메일 인증코드를 입력해 주세요."
              type="text"
              tw="h-40 w-full"
              className="input"
            />
            <button
              onClick={handleEmailCodeConfirm}
              type="button"
              tw="text-base w-160"
              className="button base"
            >
              이메일 인증
            </button>
          </div>
          <StBaseValidLabel isValid={isConfirmEmail}>
            {isConfirmEmail && Message.valid.VALID_EMAIL_CONFIRM}
            {errors.code && errors.code.message}
          </StBaseValidLabel>
          <label>닉네임</label>
          <div className="flex gap-20">
            <input
              {...register('nickname', {
                required: '닉네임을 입력해 주세요',
                onChange: handleChangeNickname,
                validate: {
                  validNickname: () =>
                    isConfirmNickname || Message.valid.REQUIRE_NICKNAME_CONFIRM,
                  requireNickname: (value) =>
                    value !== '' || Message.valid.REQUIRE_NICKNAME,
                },
              })}
              placeholder="닉네임을 입력해 주세요."
              type="text"
              tw="h-40 w-full"
              className="input"
            />
            <button
              onClick={handleNicknameConfirm}
              type="button"
              tw="text-base w-160"
              className="button base"
            >
              중복 확인
            </button>
          </div>
          <StBaseValidLabel isValid={isConfirmNickname}>
            {isConfirmNickname && Message.valid.VALID_NICKNAME_CONFIRM}
            {errors.nickname && errors.nickname.message}
          </StBaseValidLabel>
        </div>
        <div tw="w-full flex mt-30 mb-40 gap-28">
          <Link tw="w-full" to="/">
            <button tw="w-full" className="button base">
              취소
            </button>
          </Link>
          <button type="submit" tw="w-full" className="button base">
            다음
          </button>
        </div>
      </form>
    </section>
  )
}

export default SignUp
